import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Search, Plus, Clock, CheckCircle, XCircle, Calendar,
  Trash2, Phone, User, Filter, RefreshCw, AlertCircle, ArrowLeft, UserPlus, Eye, RotateCcw, FolderPlus
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { clientService } from '../services/api';
import smartScheduleService from '../services/smartScheduleService';
import QuickClientModal from './QuickClientModal';
import AvailableSlotsModal from './AvailableSlotsModal';
import { useAuth } from '../contexts/AuthContext';

const WaitingList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: 'WAITING',
    origin_type: '',
    search: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    client_name: '',
    client_phone: '',
    origin_type: 'NOVO',
    notes: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, itemId: null });
  const [clientSearch, setClientSearch] = useState('');
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showQuickClientModal, setShowQuickClientModal] = useState(false);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [selectedWaitingItem, setSelectedWaitingItem] = useState(null);

  // Debounce busca na tabela (nome, apelido ou telefone)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce client search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedClientSearch(clientSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Buscar clientes para autocomplete
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['clients-search', debouncedClientSearch],
    queryFn: () => clientService.getClients({ search: debouncedClientSearch, limit: 10 }),
    enabled: debouncedClientSearch.length >= 2 && showClientSearch
  });

  const clientResults = clientsData?.data?.data || [];

  // Selecionar cliente da busca
  const handleSelectClient = (client) => {
    setNewItem({
      ...newItem,
      client_name: client.full_name,
      client_phone: client.phone || '',
    });
    setSelectedClient(client);
    setClientSearch('');
    setShowClientSearch(false);
  };

  // Criar cliente rápido
  const handleQuickClientCreated = (client) => {
    setNewItem({
      ...newItem,
      client_name: client.full_name,
      client_phone: client.phone || '',
    });
    setSelectedClient(client);
    setShowQuickClientModal(false);
  };

  // Verificar se veio um cliente da página de primeiro contato
  useEffect(() => {
    const clientData = location.state?.clientData;
    if (clientData) {
      setNewItem({
        client_name: clientData.client_name || clientData.name || '',
        client_phone: clientData.client_phone || clientData.phone || '',
        origin_type: clientData.origin_type || 'NOVO',
        notes: clientData.notes || ''
      });
      setShowAddModal(true);
      // Limpar o state após usar
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Buscar fila de espera
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['waiting-list', filters],
    queryFn: () => smartScheduleService.getWaitingList(filters),
  });

  const waitingList = useMemo(() => {
    return data?.data?.waiting_list || [];
  }, [data]);

  const { normalList, secondWorkList, waitingCount, scheduledCount } = useMemo(() => {
    const list = data?.data?.waiting_list || [];
    const normal = list.filter(item => !item.is_second_work);
    const secondWork = list.filter(item => item.is_second_work);
    const waiting = list.filter(item => item.status === 'WAITING').length;
    const scheduled = list.filter(item => item.status === 'SCHEDULED' || item.status === 'AGENDADO').length;
    return {
      normalList: normal,
      secondWorkList: secondWork,
      waitingCount: waiting,
      scheduledCount: scheduled
    };
  }, [data]);

  const pagination = useMemo(() => {
    return data?.data?.pagination || { page: 1, limit: 50, total: 0 };
  }, [data]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data) => smartScheduleService.addToWaitingList(data),
    onSuccess: () => {
      toast.success('Adicionado à fila!');
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
      setShowAddModal(false);
      setNewItem({
        client_name: '',
        client_phone: '',
        origin_type: 'NOVO',
        notes: ''
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao adicionar à fila');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id) => smartScheduleService.removeFromWaitingList(id),
    onSuccess: () => {
      toast.success('Removido da fila!');
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao remover da fila');
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id) => smartScheduleService.updateWaitingListStatus(id, {
      status: 'WAITING',
      scheduled_appointment_id: null,
      scheduled_date: null
    }),
    onSuccess: () => {
      toast.success('Cliente retornado para a fila de espera!');
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao reabrir na fila');
    },
  });

  const convertPreProjectMutation = useMutation({
    mutationFn: (id) => api.post(`/smart-schedule/waiting-list/${id}/convert-pre-project`),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Lead convertido em Pré-Projeto com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
      navigate('/pre-projetos');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Erro ao converter lead em pré-projeto');
    }
  });

  const getStatusBadge = (status) => {
    const badges = {
      WAITING: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Aguardando' },
      AGENDADO: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle, label: 'Agendado' },
      SCHEDULED: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Agendado' },
      CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-800', icon: CheckCircle, label: 'Pré-Projeto' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Cancelado' },
      EXPIRED: { bg: 'bg-gray-100', text: 'text-gray-800', icon: AlertCircle, label: 'Expirado' },
    };
    const badge = badges[status] || badges.WAITING;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getOriginBadge = (origin) => {
    const badges = {
      NOVO: { bg: 'bg-green-100', text: 'text-green-800', label: 'Novo' },
      ANTIGO: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Antigo' },
      INDICACAO: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Indicação' },
    };
    const badge = badges[origin] || badges.NOVO;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPotentialBadge = (potential) => {
    const badges = {
      ALTO: { bg: 'bg-green-100', text: 'text-green-800', label: 'Alto' },
      MEDIO: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Médio' },
      BAIXO: { bg: 'bg-red-100', text: 'text-red-800', label: 'Baixo' },
    };
    const badge = badges[potential] || badges.MEDIO;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newItem.client_name) {
      toast.error('Nome é obrigatório');
      return;
    }
    addMutation.mutate(newItem);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/primeiro-contato"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Fila de Espera</h1>
            <p className="text-gray-600">
              {filters.status === 'WAITING'
                ? `${normalList.length} cliente(s) aguardando${secondWorkList.length > 0 ? ` • ${secondWorkList.length} retorno(s)` : ''}`
                : filters.status === 'SCHEDULED'
                ? `${waitingList.length} cliente(s) agendado(s)`
                : `${waitingCount} aguardando • ${scheduledCount} agendado(s)`}
              {' • '}Clique no calendário para agendar
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/primeiro-contato"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <User className="w-4 h-4" />
            Ver Clientes
          </Link>
          {user?.role !== 'RECEPCAO_PROJETOS' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630]"
            >
              <Plus className="w-4 h-4" />
              Adicionar à Fila
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtros:</span>
          </div>

          {/* Busca por Nome, Apelido ou Telefone */}
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, apelido ou tel..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#49943d]"
            />
          </div>

          {/* Filtro de Status */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-[#49943d]"
          >
            <option value="WAITING">Apenas Aguardando</option>
            <option value="SCHEDULED">Apenas Agendados</option>
            <option value="ALL">Todos os Status</option>
          </select>

          {/* Filtro de Origem */}
          <select
            value={filters.origin_type}
            onChange={(e) => setFilters(prev => ({ ...prev, origin_type: e.target.value }))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-[#49943d]"
          >
            <option value="">Todas Origens</option>
            <option value="NOVO">Novo</option>
            <option value="ANTIGO">Antigo</option>
            <option value="INDICACAO">Indicação</option>
          </select>

          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 font-medium text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>

          {(filters.search || filters.origin_type || filters.status !== 'WAITING') && (
            <button
              onClick={() => {
                setSearchInput('');
                setFilters({ status: 'WAITING', origin_type: '', search: '' });
              }}
              className="text-xs text-gray-500 hover:text-red-600 underline ml-auto"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : waitingList.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>Nenhum cliente na fila de espera</p>
          </div>
        ) : (
          <>
            {/* Seção: Novos Atendimentos (1º Serviço) */}
            {normalList.length > 0 && (
              <div>
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Novos Atendimentos (1º Serviço)
                    <span className="bg-blue-200 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                      {normalList.length}
                    </span>
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Potencial</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data na Fila</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {normalList.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="text-lg font-bold text-blue-600">{item.priority_score}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                                  {item.client_name}
                                  {item.client_apelido && (
                                    <span className="text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                      ({item.client_apelido})
                                    </span>
                                  )}
                                </p>
                                {item.client_phone && (
                                  <p className="text-sm text-gray-500 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {item.client_phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getPotentialBadge(item.potential)}
                          </td>
                          <td className="px-4 py-3">
                            {getOriginBadge(item.origin_type)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.created_at ? (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(item.created_at).toLocaleDateString('pt-BR')}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(item.status)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {item.status === 'WAITING' ? (
                                <>
                                  <button
                                    onClick={() => {
                                      if (item.first_contact_id) {
                                        setSelectedWaitingItem(item);
                                        setShowSlotsModal(true);
                                      } else {
                                        navigate(`/agendamentos/novo?type=levantamento&client_name=${encodeURIComponent(item.client_name)}&client_phone=${encodeURIComponent(item.client_phone || '')}`);
                                      }
                                    }}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Agendar Levantamento"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => convertPreProjectMutation.mutate(item.id)}
                                    disabled={convertPreProjectMutation.isPending}
                                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                    title="Converter diretamente em Pré-Projeto"
                                  >
                                    <FolderPlus className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ show: true, itemId: item.id })}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover da Fila"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => navigate('/agendamentos')}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Ver na Agenda"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (item.first_contact_id) {
                                        setSelectedWaitingItem(item);
                                        setShowSlotsModal(true);
                                      } else {
                                        navigate(`/agendamentos/novo?type=levantamento&client_name=${encodeURIComponent(item.client_name)}&client_phone=${encodeURIComponent(item.client_phone || '')}`);
                                      }
                                    }}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Reagendar"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => reopenMutation.mutate(item.id)}
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    title="Reabrir como Aguardando"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ show: true, itemId: item.id })}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover da Fila"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Seção: Clientes de Retorno (Segundo Trabalho) */}
            {secondWorkList.length > 0 && (
              <div>
                <div className="px-4 py-3 bg-orange-50 border-b border-orange-100">
                  <h3 className="font-semibold text-orange-900 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Retornos (Segundo Trabalho)
                    <span className="bg-orange-200 text-orange-800 text-xs px-2 py-0.5 rounded-full">
                      {secondWorkList.length}
                    </span>
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Potencial</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origem</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data na Fila</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {secondWorkList.map((item) => (
                        <tr key={item.id} className="hover:bg-orange-50">
                          <td className="px-4 py-3">
                            <span className="text-lg font-bold text-blue-600">{item.priority_score}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <RefreshCw className="w-4 h-4 text-orange-400" />
                              <div>
                                <p className="font-medium text-gray-900 flex items-center gap-1.5">
                                  {item.client_name}
                                  {item.client_apelido && (
                                    <span className="text-xs font-normal text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                      ({item.client_apelido})
                                    </span>
                                  )}
                                </p>
                                {item.client_phone && (
                                  <p className="text-sm text-gray-500 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {item.client_phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getPotentialBadge(item.potential)}
                          </td>
                          <td className="px-4 py-3">
                            {getOriginBadge(item.origin_type)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.created_at ? (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(item.created_at).toLocaleDateString('pt-BR')}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(item.status)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {item.status === 'WAITING' ? (
                                <>
                                  <button
                                    onClick={() => {
                                      if (item.first_contact_id) {
                                        setSelectedWaitingItem(item);
                                        setShowSlotsModal(true);
                                      } else {
                                        navigate(`/agendamentos/novo?type=levantamento&client_name=${encodeURIComponent(item.client_name)}&client_phone=${encodeURIComponent(item.client_phone || '')}`);
                                      }
                                    }}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Agendar Levantamento"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => convertPreProjectMutation.mutate(item.id)}
                                    disabled={convertPreProjectMutation.isPending}
                                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                    title="Converter diretamente em Pré-Projeto"
                                  >
                                    <FolderPlus className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ show: true, itemId: item.id })}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover da Fila"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => navigate('/agendamentos')}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Ver na Agenda"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (item.first_contact_id) {
                                        setSelectedWaitingItem(item);
                                        setShowSlotsModal(true);
                                      } else {
                                        navigate(`/agendamentos/novo?type=levantamento&client_name=${encodeURIComponent(item.client_name)}&client_phone=${encodeURIComponent(item.client_phone || '')}`);
                                      }
                                    }}
                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Reagendar"
                                  >
                                    <Calendar className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => reopenMutation.mutate(item.id)}
                                    className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                    title="Reabrir como Aguardando"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ show: true, itemId: item.id })}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover da Fila"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Adicionar */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Adicionar à Fila de Espera</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Busca de Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buscar Cliente
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Digite para buscar cliente..."
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setShowClientSearch(true);
                        setSelectedClient(null);
                      }}
                      onFocus={() => setShowClientSearch(true)}
                      className="w-full pl-10 pr-3 py-2 border rounded-lg"
                    />
                    <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                    {selectedClient && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedClient(null);
                          setNewItem({ ...newItem, client_name: '', client_phone: '' });
                        }}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Resultados da busca */}
                  {showClientSearch && clientSearch.length >= 2 && !selectedClient && (
                    <div className="absolute z-10 w-full max-w-md bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                      {loadingClients ? (
                        <div className="p-3 text-gray-500">Buscando...</div>
                      ) : clientResults.length > 0 ? (
                        clientResults.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => handleSelectClient(client)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b last:border-b-0"
                          >
                            <div className="font-medium">{client.full_name}</div>
                            <div className="text-sm text-gray-500">{client.phone}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-gray-500">Nenhum cliente encontrado</div>
                      )}
                    </div>
                  )}

                  {/* Cliente selecionado */}
                  {selectedClient && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-800">{selectedClient.full_name}</div>
                        <div className="text-sm text-green-600">{selectedClient.phone}</div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </div>

                {/* Botão para criar cliente rápido */}
                <button
                  type="button"
                  onClick={() => setShowQuickClientModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600"
                >
                  <UserPlus className="w-4 h-4" />
                  Criar Novo Cliente
                </button>

                {/* Nome e Telefone (preenchidos automaticamente ou manual) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Cliente *
                    </label>
                    <input
                      type="text"
                      value={newItem.client_name}
                      onChange={(e) => setNewItem({ ...newItem, client_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={newItem.client_phone}
                      onChange={(e) => setNewItem({ ...newItem, client_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Origem
                    </label>
                    <select
                      value={newItem.origin_type}
                      onChange={(e) => setNewItem({ ...newItem, origin_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                        <option value="NOVO">Novo Cliente</option>
                        <option value="ANTIGO">Cliente Antigo</option>
                        <option value="INDICACAO">Indicação</option>
                      </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Observações
                  </label>
                  <textarea
                    value={newItem.notes}
                    onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {addMutation.isPending ? 'Adicionando...' : 'Adicionar'}
                </button>
              </div>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">Confirmar Remoção</h2>
            <p className="text-gray-600 mb-6">Tem certeza que deseja remover este cliente da fila de espera?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, itemId: null })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  removeMutation.mutate(deleteConfirm.itemId);
                  setDeleteConfirm({ show: false, itemId: null });
                }}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação Rápida de Cliente */}
      {showQuickClientModal && (
        <QuickClientModal
          onClose={() => setShowQuickClientModal(false)}
          onSuccess={handleQuickClientCreated}
        />
      )}

      {/* Modal de Agendamento Inteligente */}
      {showSlotsModal && selectedWaitingItem && (
        <AvailableSlotsModal
          isOpen={showSlotsModal}
          onClose={() => {
            setShowSlotsModal(false);
            setSelectedWaitingItem(null);
          }}
          firstContact={{
            id: selectedWaitingItem.first_contact_id,
            client_name: selectedWaitingItem.client_name,
            potential: selectedWaitingItem.potential,
            origin_type: selectedWaitingItem.origin_type,
            priority_score: selectedWaitingItem.priority_score,
            service_type: selectedWaitingItem.service_type,
            estimated_budget: selectedWaitingItem.estimated_budget,
            is_second_work: selectedWaitingItem.is_second_work
          }}
          onScheduled={() => {
            queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
            toast.success('Levantamento agendado com sucesso!');
            setShowSlotsModal(false);
            setSelectedWaitingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default WaitingList;
